import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Wordmark } from "@/components/common/ui";
import { EnquiryCard, KIND_LABELS } from "@/components/admin/EnquiryCard";
import { FeedPanel } from "@/components/admin/FeedPanel";
import { TeamPanel } from "@/components/admin/TeamPanel";
import { VisitorPanel } from "@/components/admin/VisitorPanel";
import { SplashThemePanel } from "@/components/admin/SplashThemePanel";
import { useAuth } from "@/context/AuthContext";
import { useBases, useMeta } from "@/hooks/useData";
import { api, errorMessage } from "@/lib/api";

const STATUSES = [["", "All"], ["new", "New"], ["contacted", "Contacted"], ["closed", "Closed"]];

const Chip = ({ active, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`rounded-full px-4 py-1.5 text-[13px] transition-colors ${active ? "bg-ink text-white" : "bg-white text-neutral-700 hover:text-ink"}`}>
    {children}
  </button>
);

export default function AdminPage() {
  const { user, logout } = useAuth();
  const bases = useBases();
  const { meta, reload } = useMeta();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [kind, setKind] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/enquiries");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const setItemStatus = async (id, s) => {
    try {
      const { data } = await api.patch(`/admin/enquiries/${id}`, { status: s });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry? This can't be undone.")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const kinds = useMemo(() => [...new Set(items.map((x) => x.kind))], [items]);
  const shown = items.filter((x) => (!status || x.status === status) && (!kind || x.kind === kind));
  const newCount = items.filter((x) => x.status === "new").length;
  const baseName = (id) => bases.find((b) => b.id === id)?.name;

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-page">
      <header className="sticky top-0 z-20 border-b border-neutral-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-5">
          <Link to="/" data-testid="admin-home-link"><Wordmark /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-[13px] text-neutral-500 sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} data-testid="logout-button"
              className="inline-flex h-9 items-center gap-2 rounded-full border border-neutral-300 px-4 text-[13px] text-ink hover:border-ink">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] space-y-8 px-5 py-8 sm:py-10">
        <div>
          <p className="eyebrow">Private team inbox</p>
          <h1 className="mt-2 text-3xl font-normal text-ink sm:text-4xl" data-testid="admin-heading">Website enquiries</h1>
          <p className="mt-1 text-[15px] text-neutral-600" data-testid="admin-counts">{items.length} total · {newCount} new</p>
        </div>

        <FeedPanel meta={meta} reload={reload} />

        <SplashThemePanel />

        <TeamPanel />

        <VisitorPanel />

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1 rounded-full bg-neutral-200/60 p-1 self-start" data-testid="admin-status-filter">
            {STATUSES.map(([v, l]) => <Chip key={l} active={status === v} onClick={() => setStatus(v)} testId={`admin-status-${l.toLowerCase()}`}>{l}</Chip>)}
          </div>
          {kinds.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="admin-kind-filter">
              <Chip active={!kind} onClick={() => setKind("")} testId="admin-kind-all">All types</Chip>
              {kinds.map((k) => <Chip key={k} active={kind === k} onClick={() => setKind(k)} testId={`admin-kind-${k}`}>{KIND_LABELS[k] || k}</Chip>)}
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="enquiry-list">
          {loading && <p className="text-neutral-500" data-testid="admin-loading">Loading enquiries…</p>}
          {!loading && !shown.length && (
            <div className="rounded-md border border-dashed border-neutral-300 bg-white p-10 text-center" data-testid="admin-empty">
              <Inbox className="mx-auto h-8 w-8 text-neutral-400" />
              <p className="mt-3 text-lg text-ink">No enquiries here yet</p>
              <p className="text-[14px] text-neutral-600">New website forms land here the moment someone sends one.</p>
            </div>
          )}
          {shown.map((e) => <EnquiryCard key={e.id} e={e} baseName={baseName(e.base_id)} onStatus={setItemStatus} onDelete={remove} />)}
        </div>
      </main>
    </div>
  );
}
